import { computed, inject, onUnmounted, provide, type Ref, ref, shallowReactive, shallowRef } from "vue";

export interface Message { isFehler: Ref<boolean> }

export default class ValidationGroup {

  children = shallowReactive<ValidationGroup[]>([]);
  callbacks = ref<Array<() => Promise<void>>>([]);
  parent = shallowRef<ValidationGroup>();

  errorIdentifier = ref<string[]>([]);

  root = computed(() => {
    let p: ValidationGroup = this;
    while (p.parent.value) {
      p = p.parent.value;
    }
    if (p instanceof ValidationRoot)
      return p;
    else
      return null;
  });

  errors = computed(() => {
    if (!this.root.value)
      return [];
      
    const errors = [];
    for (const sel of this.errorIdentifier.value) {
      const err = this.root.value.messageMap.value.get(sel);
      if (err) {
        errors.push(err);
      }
    }
    return errors
  });

  hasError = computed(() => {
    return this.errors.value.filter((e) => e.isFehler.value).length > 0;
  });

  hasWarning = computed(() => {
    return this.errors.value.filter((e) => !e.isFehler.value).length > 0;
  });

  hasChildErrors = computed(() => {
    return this.children.some(g => g.hasError.value || g.hasChildErrors.value);
  });

  hasChildWarning = computed(() => {
    return this.children.some(g => g.hasWarning.value || g.hasChildWarning.value);
  });

  
  constructor(parent: ValidationGroup) {
    this.parent.value = parent;
  }

  async callCallbacks() {
    for (const callback of this.callbacks.value) {
      await callback();
    }
  }

  matchesIdentifier(errorIdentifier) {
    return this.errorIdentifier.value.some(e => e == errorIdentifier);
  }

  
  groupExists(errorIdentifier) {
    let grpStack: ValidationGroup[] = [this];
    while (grpStack.length > 0) {
      const grp = grpStack.pop();
      if (grp.matchesIdentifier(errorIdentifier)) {
        return true; // EarlyExit -> matchender Identifier gefunden.
      }
      grpStack.push(...grp.children);
    }
    return false
  }

  async callCallbacksForIdentifier(errorIdentifier) {
    if (this.matchesIdentifier(errorIdentifier) && (this.hasError.value || this.hasWarning.value)) {
      const callStack = [];
      let p = this.parent.value;
      while (p) {
        callStack.push(p);
        p = p.parent.value;
      }
      while (callStack.length > 0) {
        await callStack.pop().callCallbacks(); //Callbacks von Oben nach unten durchgehen. z.B. Reiter öffnen -> Zeile ausklappen -> zum Fehler springen
      }
      await this.callCallbacks();
      return true;
    }
    return false;
  }

  async callCallbacksRecursive(errorIdentifier) {
    let grpStack: ValidationGroup[] = [this];
    while (grpStack.length > 0) {
      const grp = grpStack.pop();
      await grp.callCallbacksForIdentifier(errorIdentifier)
      grpStack.push(...grp.children);
    }
  }
}

export function validationGroupProvInjFactory(){
  const parentValidationGroup: ValidationGroup | null = inject("validationGroup", null);
  const grp = new ValidationGroup(parentValidationGroup);

  parentValidationGroup?.children.push(grp);

  onUnmounted(() => {
    if (!parentValidationGroup)
      return;
    const idx = parentValidationGroup.children.indexOf(grp);
    if (idx !== -1) {
      parentValidationGroup.children.splice(idx, 1);
    }
  });

  provide("validationGroup", grp);
  return grp;
}

export class ValidationRoot extends ValidationGroup {
  messageMap = shallowRef(new Map<string, Message>());

  constructor() {
    super(undefined);
  }
}